import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "../components/Head";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { indicatorToSlug, sectionToSlug, slugToIndicator, slugToSection } from "../constants/filterSlugs";
import { SECTION_NAMES } from "../content/site";
import SelectField from "../components/SelectField";
import loadEcharts from "../functions/echartsLoader";
import {
  CAGR_LABELS,
  CAGR_ORDER,
  CAGR_WINDOWS,
  INDICATORS,
  calculateReferenceValue,
  deduplicateData,
  determineCagrContext,
  determineContext,
  formatValue,
  getCagrTitle,
  prepareBarData,
  prepareBoxData,
  prepareCagrBarData,
  buildBoxplotOption,
  buildGradientBarOption
} from "../functions/benchmarkCalculations";
import { fetchBenchmarks, fetchStats, fetchCagrDrilldown  } from "../services/api";
import { useAuth } from "../context/AuthContext";
import "./style/analytics.css";



const INDICATOR_INFO = {
  ROA: {
    label: "ROA – Rentabilita celkových aktiv",
    description: "Ukazuje, kolik provozního zisku firma generuje na 1 Kč aktiv.",
    formula: "EBIT / Aktiva celkem",
  },
  ROE: {
    label: "ROE – Rentabilita vlastního kapitálu",
    description: "Měří výnosnost vlastního kapitálu před zdaněním.",
    formula: "Zisk před zdaněním / Vlastní kapitál",
  },
  ROS: {
    label: "ROS – Rentabilita tržeb",
    description: "Sleduje, jaká část tržeb zůstává jako zisk před zdaněním.",
    formula: "Zisk před zdaněním / Tržby celkem",
  },
  "EBITDA marže": {
    label: "EBITDA marže – EBITDA/Tržby",
    description: "Poměřuje provozní výkonnost očištěnou o odpisy k tržbám.",
    formula: "EBITDA / Tržby celkem",
  },
  "Provozní marže": {
    label: "Provozní marže ",
    description: "Ukazuje efektivitu hlavní činnosti podniku.",
    formula: "EBIT / Tržby celkem",
  },
  "Likvidita běžná": {
    label: "Likvidita běžná",
    description: "Schopnost krýt krátkodobé závazky oběžnými aktivy.",
    formula: "Oběžná aktiva / Krátkodobé závazky",
  },
  "Likvidita pohotová": {
    label: "Likvidita pohotová",
    description: "Oceňuje schopnost platit závazky bez nutnosti prodeje zásob.",
    formula: "(Peněžní prostředky + Krátkodobé a dlouhodobé pohledávky) / Krátkodobé závazky",
  },
  "Likvidita okamžitá": {
    label: "Likvidita okamžitá",
    description: "Okamžitá schopnost splácet dluhy hotovostí.",
    formula: "Peněžní prostředky / Krátkodobé závazky",
  },
  "Zadluženost celková": {
    label: "Zadluženost celková",
    description: "Celkový podíl cizích zdrojů na financování majetku.",
    formula: "(Aktiva celkem − Vlastní kapitál) / Aktiva celkem",
  },
  "Zadluženost dlouhodobá": {
    label: "Zadluženost dlouhodobá",
    description: "Podíl dlouhodobých závazků na celkových aktivech.",
    formula: "Dlouhodobé závazky / Aktiva celkem",
  },
  "Obrat aktiv": {
    label: "Obrat aktiv",
    description: "Jak efektivně firma využívá aktiva k tvorbě tržeb.",
    formula: "Tržby celkem / Aktiva celkem",
  },
  "Obrat zásob": {
    label: "Obrat zásob",
    description: "Rychlost, jakou se zásoby proměňují v tržby.",
    formula: "Tržby celkem / Zásoby",
  },
  "Doba obratu pohledávek": {
    label: "Doba obratu pohledávek (dny)",
    description: "Za kolik dní v průměru firmě zaplatí odběratelé.",
    formula: "Krátkodobé pohledávky / Tržby celkem × 360",
  },
  "Doba obratu závazků": {
    label: "Doba obratu závazků (dny)",
    description: "Za kolik dní v průměru firma platí své krátkodobé závazky.",
    formula: "Krátkodobé závazky / Tržby celkem × 360",
  },
  "Cyklus peněz": {
    label: "Cyklus peněz (dny)",
    description: "Kolik dní má firma peníze vázané v provozu. Záporná hodnota znamená, že provoz financují dodavatelé a zálohy.",
    formula: "Doba obratu pohledávek + Doba obratu zásob − Doba obratu závazků",
  },
  "Marže přidané hodnoty": {
    label: "Marže přidané hodnoty",
    description: "Jaká část tržeb zůstane po zaplacení materiálu, zboží, energií a služeb.",
    formula: "(Tržby celkem − Výkonová spotřeba) / Tržby celkem",
  },
  "Osobní náklady / přidaná hodnota": {
    label: "Osobní náklady / přidaná hodnota",
    description: "Jakou část přidané hodnoty spotřebují mzdy a odvody. Nižší hodnota znamená vyšší produktivitu práce.",
    formula: "Osobní náklady / (Tržby celkem − Výkonová spotřeba)",
  },
  "Podíl osobních nákladů": {
    label: "Podíl osobních nákladů na tržbách",
    description: "Kolik z každé koruny tržeb jde na mzdy a odvody.",
    formula: "Osobní náklady / Tržby celkem",
  },
  "Meziroční růst tržeb": {
    label: "Meziroční růst tržeb",
    description: "O kolik procent se tržby typické firmy změnily proti předchozímu roku.",
    formula: "Tržby celkem / Tržby celkem v předchozím roce − 1",
  },
};

const CAGR_METRIC_INFO = {
  aktiva: {
    label: CAGR_LABELS.aktiva,
    description: "Průměrné roční tempo růstu celkových aktiv ve zvoleném období.",
  },
  trzby: {
    label: CAGR_LABELS.trzby,
    description: "Průměrné roční tempo růstu tržeb z hlavní činnosti.",
  },
  ebitda: {
    label: CAGR_LABELS.ebitda,
    description: "Průměrné roční tempo růstu EBITDA.",
  },
  zasoby: {
    label: CAGR_LABELS.zasoby,
    description: "Průměrné roční tempo změny stavu zásob.",
  },
  zadluzenost: {
    label: CAGR_LABELS.zadluzenost,
    description: "Průměrná roční změna zadluženosti (nižší = nižší zadluženost).",
  },
};

const ChartHeader = ({ title, description, tooltip }) => (
<div
  className="chart-header"
  style={{
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginBottom: "0.75rem",
  }}
>
  {/* TOP ROW */}
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
    }}
  >
    <strong style={{ fontSize: "1.05rem", color: "#0b1f2e" }}>
      {title}
    </strong>

    {tooltip && (
      <span
        title={tooltip}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "22px",
          height: "22px",
          borderRadius: "50%",
          background: "#eef3f2",
          color: "#027360",
          fontWeight: "bold",
          cursor: "help",
        }}
      >
        ?
      </span>
    )}
  </div>

  {/* BOTTOM ROW — FULL WIDTH */}
  {description && (
    <p
      style={{
        margin: 0,
        color: "#5f6b6f",
        fontSize: "0.95rem",
        width: "100%",
        lineHeight: 1.45,
      }}
    >
      {description}
    </p>
  )}
</div>

);

const BenchmarkPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { ukazatel, obor } = useParams();

  // Filters
  const [selectedPeriod, setSelectedPeriod] = useState("all"); 
  
  // Lists
  const [allSections, setAllSections] = useState([]);
  const [allL1, setAllL1] = useState([]);
  const [allL2, setAllL2] = useState([]);

  const [lvl1Options, setLvl1Options] = useState([]);
  const [lvl2Options, setLvl2Options] = useState([]);

  // Selections
  const [selectedSekce, setSelectedSekce] = useState(() => slugToSection(obor) || "Vše");
  const [selectedL1, setSelectedL1] = useState("Vše");
  const [selectedL2, setSelectedL2] = useState("Vše");
  
  const [selectedIndicator, setSelectedIndicator] = useState(() => slugToIndicator(ukazatel) || "ROA");
  const [isTableOpen, setIsTableOpen] = useState(false);
  // Data
  const [benchmarkData, setBenchmarkData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Refs
  const chartsReady = useRef(false);
  const barChartRef = useRef(null);
  const boxChartRef = useRef(null);
  const [cagrWindowYears, setCagrWindowYears] = useState(6);
  const [cagrSekce, setCagrSekce] = useState("Vše");
  // ==========================================
  // --- CAGR ADDITION: State & Logic ---
  // ==========================================
  
  const [selectedCagrMetric, setSelectedCagrMetric] = useState(CAGR_ORDER[0]);
  const [cagrData, setCagrData] = useState([]);
  const [cagrLoading, setCagrLoading] = useState(false);
  const cagrChartRef = useRef(null);
  const [cagrL1, setCagrL1] = useState("Vše");
  const cagrContext = useMemo(
    () => determineCagrContext(cagrSekce, cagrL1),
    [cagrSekce, cagrL1]
  );
  const cagrTitle = useMemo(
    () => getCagrTitle(cagrContext, cagrSekce),
    [cagrContext, cagrSekce]
  );

  const isPublicBenchmark = location.pathname.startsWith("/benchmark-obory");
  const isRestrictedView = isPublicBenchmark && !user;

  const openAuthPrompt = (mode = "register") => {
    const params = new URLSearchParams(location.search);
    params.set("auth", mode);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  const cagrMeta = useMemo(
    () => ({
      label: CAGR_METRIC_INFO[selectedCagrMetric]?.label || selectedCagrMetric,
      description: CAGR_METRIC_INFO[selectedCagrMetric]?.description || "",
    }),
    [selectedCagrMetric]
  );

  const periodOptions = useMemo(
    () => [
      { value: "2023-2024", label: "2023-2025 (Aktuální)" },
      { value: "2021-2024", label: "2021-2025 (Post-COVID)" },
      { value: "all", label: "2016-2025 (Dlouhodobé)" },
    ],
    []
  );

  const periodSelectOptions = useMemo(
    () =>
      periodOptions.map((option) => ({
        ...option,
        label:
          isRestrictedView && option.value !== "all"
            ? `${option.label} 🔒`
            : option.label,
      })),
    [periodOptions, isRestrictedView]
  );

  const sectionOptions = useMemo(
    () => [
      { value: "Vše", label: "Všechny sekce" },
      ...allSections.map((s) => ({
        value: s.Sekce,
        label: `${s.Sekce} - ${s["Sekce název"]}`,
      })),
    ],
    [allSections]
  );

  const level1SelectOptions = useMemo(
    () => [
      { value: "Vše", label: "Vše" },
      ...lvl1Options.map((l) => ({
        value: l["1 úroveň"],
        label: `${l["1 úroveň"]} - ${l["1 úroveň název"]}${isRestrictedView ? " 🔒" : ""}`,
      })),
    ],
    [lvl1Options, isRestrictedView]
  );

  const level2SelectOptions = useMemo(
    () => [
      { value: "Vše", label: "Vše" },
      ...lvl2Options.map((l) => ({
        value: l["2 úroveň ID"],
        label: `${l["2 úroveň ID"]} - ${l["2 úroveň název"]}${isRestrictedView ? " 🔒" : ""}`,
      })),
    ],
    [lvl2Options, isRestrictedView]
  );

  const indicatorOptions = useMemo(
    () =>
      INDICATORS.map((i) => ({
        value: i,
        label: INDICATOR_INFO[i]?.label || i,
      })),
    []
  );

  const currentIndicatorMeta = useMemo(
    () => ({
      label: INDICATOR_INFO[selectedIndicator]?.label || selectedIndicator,
      description: INDICATOR_INFO[selectedIndicator]?.description || "",
      formula: INDICATOR_INFO[selectedIndicator]?.formula || "",
    }),
    [selectedIndicator]
  );

  const selectedSectionFullLabel = useMemo(() => {
    if (selectedSekce === "Vše") return "Všechny sekce";
    const match = allSections.find((s) => s.Sekce === selectedSekce);
    return match ? ` ${match["Sekce název"]}` : selectedSekce;
  }, [selectedSekce, allSections]);

  const selectedL1FullLabel = useMemo(() => {
  if (selectedL1 === "Vše") return "Všechny obory (L1)";
  const match = allL1.find(
    l =>
      String(l["1 úroveň"]) === String(selectedL1) &&
      l.Sekce === selectedSekce
  );
  return match
    ? `${match["1 úroveň"]} - ${match["1 úroveň název"]}`
    : selectedL1;
}, [selectedL1, selectedSekce, allL1]);

const selectedL2FullLabel = useMemo(() => {
  if (selectedL2 === "Vše") return "Všechny dílčí obory (L2)";
  const match = allL2.find(
    l =>
      String(l["2 úroveň ID"]) === String(selectedL2) &&
      String(l["1 úroveň"]) === String(selectedL1) &&
      l.Sekce === selectedSekce
  );
  return match
    ? `${match["2 úroveň ID"]} - ${match["2 úroveň název"]}`
    : selectedL2;
}, [selectedL2, selectedL1, selectedSekce, allL2]);


  const selectedPeriodLabel = useMemo(
    () => periodOptions.find((p) => p.value === selectedPeriod)?.label || selectedPeriod,
    [periodOptions, selectedPeriod]
  );

  const cagrWindowLabel = useMemo(
    () => CAGR_WINDOWS.find((w) => w.value === cagrWindowYears)?.label || `${cagrWindowYears} let`,
    [cagrWindowYears]
  );

  const indicatorTooltipText = useMemo(() => {
    const base = currentIndicatorMeta.description ? `${currentIndicatorMeta.description}. ` : "";
    const formula = currentIndicatorMeta.formula ? `Výpočet: ${currentIndicatorMeta.formula}.` : "";
    return `${base}${formula}`.trim();
  }, [currentIndicatorMeta]);

  const handleRestrictedSelect = (nextValue, setter, allowedValues = []) => {
    if (!isRestrictedView || allowedValues.includes(nextValue)) {
      setter(nextValue);
      return;
    }
    openAuthPrompt("register");
  };

  useEffect(() => {
    if (!isRestrictedView) return;
    if (selectedPeriod !== "all") setSelectedPeriod("all");
    if (selectedL1 !== "Vše") setSelectedL1("Vše");
    if (selectedL2 !== "Vše") setSelectedL2("Vše");
  }, [isRestrictedView, selectedL1, selectedL2, selectedPeriod]);


  useEffect(() => {
      const sectionFromPath = slugToSection(obor) || "Vše";
      const indicatorFromPath = slugToIndicator(ukazatel) || "ROA";

      setSelectedSekce((prev) => (prev !== sectionFromPath ? sectionFromPath : prev));
      setSelectedIndicator((prev) => (prev !== indicatorFromPath ? indicatorFromPath : prev));
    }, [obor, ukazatel]);

  useEffect(() => {
    const sectionSlug = sectionToSlug(selectedSekce);
    const indicatorSlug = indicatorToSlug(selectedIndicator);
    const shouldUseBasePath = !sectionSlug && indicatorSlug === "roa";
    const nextPathname = shouldUseBasePath
      ? "/benchmark-obory"
      : `/benchmark-obory/${indicatorSlug}${sectionSlug ? `/${sectionSlug}` : ""}`;

    const params = new URLSearchParams(location.search);
    const authParam = params.get("auth");
    const nextSearch = authParam ? `?auth=${authParam}` : "";

    if (`${location.pathname}${location.search}` !== `${nextPathname}${nextSearch}`) {
      navigate(`${nextPathname}${nextSearch}`, { replace: true });
    }
  }, [selectedIndicator, selectedSekce, location.pathname, location.search, navigate]);


  // --- 1. Init ---
  useEffect(() => {
    loadEcharts().then(() => { chartsReady.current = true; });
    const loadStructure = async () => {
        try {
            const [s, l1, l2] = await Promise.all([
                fetchStats("sections", "all"),
                fetchStats("level1", "all"),
                fetchStats("level2", "all")
            ]);
            setAllSections(s);
            setAllL1(l1);
            setAllL2(l2);
        } catch (e) { console.error(e); }
    };
    loadStructure();
  }, []);

  // --- 2. Cascade Filters ---
  useEffect(() => {
    if (selectedSekce === "Vše") {
        setLvl1Options([]);
    } else {
        const opts = allL1.filter(l => l.Sekce === selectedSekce).sort((a,b) => a["1 úroveň"] - b["1 úroveň"]);
        setLvl1Options(opts);
    }
    setSelectedL1("Vše");
    setSelectedL2("Vše");
  }, [selectedSekce, allL1]);

  useEffect(() => {
    if (selectedL1 === "Vše") {
        setLvl2Options([]);
    } else {
        const opts = allL2.filter(l => l.Sekce === selectedSekce && String(l["1 úroveň"]) === String(selectedL1));
        opts.sort((a,b) => String(a["2 úroveň ID"]).localeCompare(String(b["2 úroveň ID"])));
        setLvl2Options(opts);
    }
    setSelectedL2("Vše");
  }, [selectedL1, allL2, selectedSekce]);


  // --- 3. Determine Context & Fetch ---
  const currentContext = useMemo(
    () => determineContext(selectedSekce, selectedL1, selectedL2),
    [selectedSekce, selectedL1, selectedL2]
  );

  const contextLabel = useMemo(() => {
    if (currentContext === "sections") return "sekcí NACE";
    if (currentContext === "lvl1") return `oborů spadajících pod ${selectedSectionFullLabel}`;
    if (currentContext === "lvl2") return  `dílčích oborů skupiny ${selectedL1FullLabel}`;
    return `detailu oboru ${selectedL2FullLabel}`;
  }, [currentContext, selectedSectionFullLabel, selectedL1FullLabel, selectedL2FullLabel]);

  const medianDescription = useMemo(() => {
    const cleanedNote =
      "Hodnota ukazuje medián vybraného ukazatele napříč vybranými roky; hodnoty jsou očištěny od extrémů.";

    if (currentContext === "sections") {
      return `Srovnání sekcí NACE za období ${selectedPeriodLabel}. ${cleanedNote}`;
    }

    if (currentContext === "lvl1") {
      return `Srovnání oborů spadajících pod sekci ${selectedSectionFullLabel} za období ${selectedPeriodLabel}. ${cleanedNote}`;
    }

    if (currentContext === "lvl2") {
      return `Srovnání dílčích oborů skupiny ${selectedL1FullLabel} za období ${selectedPeriodLabel}. ${cleanedNote}`;
    }

    return `Detailní pohled na obor ${selectedL2FullLabel} za období ${selectedPeriodLabel}. ${cleanedNote}`;
  }, [currentContext, selectedSectionFullLabel, selectedPeriodLabel, selectedL1FullLabel, selectedL2FullLabel]);
  useEffect(() => {
    setLoading(true);
    let levelToFetch = "sekce"; 
    if (currentContext === "lvl1") levelToFetch = "l1";
    if (currentContext === "lvl2" || currentContext === "single") levelToFetch = "l2";

    fetchBenchmarks(selectedPeriod, levelToFetch)
      .then(res => {
          let data = res.data;
          
          if (currentContext === "lvl1") {
              data = data.filter(r => r.Sekce === selectedSekce);
          } else if (currentContext === "lvl2" || currentContext === "single") {
              data = data.filter(r => r.Sekce === selectedSekce && String(r["1 úroveň"]) === String(selectedL1));
              
              if (currentContext === "single") {
                  data = data.filter(r => String(r["2 úroveň ID"]) === String(selectedL2));
              }
          }
          
          // --- APPLY DEDUPLICATION HERE ---
          // This will remove duplicates like 68.2 vs 68.20 keeping the one with higher 'n'
          const uniqueData = deduplicateData(data, currentContext);
          
          setBenchmarkData(uniqueData);
      })
      .catch(console.error)
      .finally(() => setLoading(false));

  }, [selectedPeriod, currentContext, selectedSekce, selectedL1, selectedL2]);


  // --- 4. Prepare Visual Data ---

  // Bar Data
  const barData = useMemo(
    () => prepareBarData(benchmarkData, selectedIndicator, currentContext),
    [benchmarkData, selectedIndicator, currentContext]
  );

  // Boxplot Data
  const boxData = useMemo(
    () => prepareBoxData(benchmarkData, selectedIndicator, currentContext),
    [benchmarkData, selectedIndicator, currentContext]
  );

  // Reference Value
  const referenceValue = useMemo(
    () => calculateReferenceValue(barData),
    [barData]
  );


    useEffect(() => {
    setCagrLoading(true);

    const parentId =
        cagrContext === "sekce"
        ? cagrSekce
        : cagrContext === "l1"
        ? cagrL1
        : null;

    fetchCagrDrilldown(cagrContext, parentId, cagrWindowYears)
        .then(data => setCagrData(Array.isArray(data) ? data : []))
        .catch(() => setCagrData([]))
        .finally(() => setCagrLoading(false));

    }, [cagrContext, cagrSekce, cagrL1, cagrWindowYears]);


    // 2. Prepare CAGR Chart Data
    const cagrBarData = useMemo(
      () => prepareCagrBarData(cagrData, selectedCagrMetric, cagrWindowYears, cagrContext, allSections, allL1, allL2),
      [cagrData, selectedCagrMetric, cagrWindowYears, cagrContext, allSections, allL1, allL2]
    );

  

  // 3. Render CAGR Chart
  useEffect(() => {
      if (!chartsReady.current || !window.echarts) return;

      const timer = setTimeout(() => {
          if (cagrLoading) return;

          if (cagrChartRef.current && cagrBarData.length > 0) {
              const chart = window.echarts.getInstanceByDom(cagrChartRef.current) || window.echarts.init(cagrChartRef.current);
              const title = `${CAGR_LABELS[selectedCagrMetric]} – ${cagrTitle}`;
              chart.setOption(buildGradientBarOption(title, cagrBarData, null, selectedCagrMetric), true);
              chart.resize();
          }
      }, 50);

      return () => clearTimeout(timer);
  }, [cagrBarData, selectedCagrMetric, cagrLoading, cagrTitle]);
  useEffect(() => {
    setCagrL1("Vše");
    }, [cagrSekce]);

  // --- 5. Render Charts ---
  useEffect(() => {
      // Если Echarts не готов, выходим
      if (!chartsReady.current || !window.echarts) return;

      // Таймер на 50мс дает DOM-дереву обновиться (особенно важно при переключении loading)
      const timer = setTimeout(() => {
          // Проверяем loading внутри, чтобы не рисовать на старых данных
          if (loading) return;

          // Bar Chart
          if (barChartRef.current && barData.length > 0) {
              const chart = window.echarts.getInstanceByDom(barChartRef.current) || window.echarts.init(barChartRef.current);
              const title = `Medián ${selectedIndicator} (${currentContext})`;
              // Параметр 'notMerge: true' (второй аргумент) крайне важен, чтобы старые данные не "подмешивались"
              chart.setOption(buildGradientBarOption(title, barData, referenceValue, selectedIndicator), true);
              chart.resize();
          }

          // Boxplot
          if (boxChartRef.current && boxData.length > 0) {
              const chart = window.echarts.getInstanceByDom(boxChartRef.current) || window.echarts.init(boxChartRef.current);
              chart.setOption(buildBoxplotOption(`Rozptyl hodnot (p10–p90)`, boxData, selectedIndicator), true);
              chart.resize();
          }
      }, 50);

      return () => clearTimeout(timer);
  }, [barData, boxData, selectedIndicator, loading, referenceValue]);


  // --- Title Logic ---
  const getContextTitle = () => {
      if (currentContext === "sections") return "Rychlý přehled napříč sekcemi";
      if (currentContext === "lvl1") return `Rychlý přehled: ${selectedSectionFullLabel}`;
      if (currentContext === "lvl2") return `Rychlý přehled: ${selectedL1FullLabel}`;
      return `Detail kategorie (${selectedL2FullLabel})`;
  };

  const seoTitle = useMemo(() => {
  const indicatorLabel = selectedIndicator;
  if (currentContext === "sections") {
    return selectedIndicator === "ROA"
      ? "Finanční benchmark oborů v Česku | ROA, ROE, EBITDA"
      : `${indicatorLabel} podle oborů v Česku – benchmark | FinPilot`;
  }
  if (currentContext === "lvl1") {
    return `${indicatorLabel} v oboru ${(SECTION_NAMES[selectedSekce] || selectedSectionFullLabel)} – benchmark | FinPilot`;
  }
  if (currentContext === "lvl2") {
    return `Finanční ukazatele – ${selectedSectionFullLabel}, skupina ${selectedL1FullLabel} | FinPilot`;
  }
  return `Detailní finanční statistiky – ${selectedL2FullLabel} | FinPilot`;
}, [currentContext, selectedIndicator, selectedSekce, selectedSectionFullLabel, selectedL1FullLabel, selectedL2FullLabel]);

const seoH1 = useMemo(() => {
  const indicatorLabel = selectedIndicator;
  if (currentContext === "sections") {
    return selectedIndicator === "ROA" ? "Finanční benchmark oborů v Česku" : `${indicatorLabel} podle oborů v Česku`;
  }
  if (currentContext === "lvl1") return `${indicatorLabel}: ${(SECTION_NAMES[selectedSekce] || selectedSectionFullLabel)} – srovnání oborů`;
  return "Finanční benchmark oborů v Česku";
}, [currentContext, selectedIndicator, selectedSekce, selectedSectionFullLabel]);

const seoDescription = useMemo(() => {
  const indicatorLabel = selectedIndicator;
  const where = currentContext === "lvl1" ? ` v sekci ${(SECTION_NAMES[selectedSekce] || selectedSectionFullLabel)}` : " podle oborů";
  return `${indicatorLabel} českých firem${where}: mediány, kvartily a srovnání oborů CZ-NACE za období 2016–2025. Data z účetních závěrek více než 25 000 firem.`;
}, [selectedIndicator, currentContext, selectedSekce, selectedSectionFullLabel]);

const canonicalUrl = useMemo(() => {
  const sectionSlug = sectionToSlug(selectedSekce);
  const indicatorSlug = indicatorToSlug(selectedIndicator) || "roa";

  if (sectionSlug) {
    return `https://finpilot.cz/benchmark-obory/${indicatorSlug}/${sectionSlug}`;
  }

  if (indicatorSlug === "roa") {
    return "https://finpilot.cz/benchmark-obory";
  }

  return `https://finpilot.cz/benchmark-obory/${indicatorSlug}`;
}, [selectedIndicator, selectedSekce]);

const benchmarkSchema = useMemo(() => ({
  "@context": "https://schema.org",
  "@type": "Dataset",
  name: seoTitle,
  description: seoDescription,
  url: canonicalUrl,
  inLanguage: "cs-CZ",
  isAccessibleForFree: true,
  creator: {
    "@type": "Organization",
    name: "FinPilot",
    url: "https://finpilot.cz",
    logo: "https://finpilot.cz/logo512.png"
  },
  publisher: {
    "@type": "Organization",
    name: "FinPilot",
    url: "https://finpilot.cz",
    logo: "https://finpilot.cz/logo512.png"
  },
  keywords: ["benchmark oborů", "NACE", "ROA", "ROE", "CAGR", "finanční analýza"]
}), [canonicalUrl, seoDescription, seoTitle]);


  return (
    <div className="stats-echarts-page">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="robots" content="index,follow" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://finpilot.cz/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://finpilot.cz/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(benchmarkSchema)}</script>
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

<header className="page-head">
  <div>
    <p className="eyebrow">Analýza trhu</p>

    <h1>{seoH1}</h1>

    <p className="lead">
      Srovnání finančních ukazatelů českých firem podle oborů,
      sektorů a klasifikace NACE.
    </p>

    <p className="sublead">
      Data vycházejí z účetních výkazů více než <strong>25&nbsp;000 </strong>
       českých podniků za období <strong>2016–2025</strong>.
    </p>
  </div>
</header>



      {/* --- FILTERS --- */}
      <section className="card" style={{ marginBottom: "2rem" }}>
        <div className="filters" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
            

            <SelectField
              className="control-group"
              label="Období dat"
              value={selectedPeriod}
              onChange={(e) =>
                handleRestrictedSelect(e.target.value, setSelectedPeriod, ["all"])
              }
              options={periodSelectOptions}
              helperText={
                isRestrictedView
                  ? "Další období jsou dostupná po registraci zdarma."
                  : undefined
              }
              selectClassName={`big-select${isRestrictedView ? " is-locked" : ""}`}
            />

            <SelectField
              className="control-group"
              label="Sekce dle NACE"
              value={selectedSekce}
              onChange={(e) => setSelectedSekce(e.target.value)}
              options={sectionOptions}
            />

            <SelectField
              className="control-group"
              label="Obor dle NACE"
              value={selectedL1}
              onChange={(e) =>
                handleRestrictedSelect(e.target.value, setSelectedL1, ["Vše"])
              }
              options={level1SelectOptions}
              disabled={selectedSekce === "Vše"}
              helperText={
                isRestrictedView
                  ? "Detailnější obory jsou dostupné po registraci."
                  : undefined
              }
              selectClassName={`big-select${isRestrictedView ? " is-locked" : ""}`}
            />

            <SelectField
              className="control-group"
              label="Dilčí obor dle NACE"
              value={selectedL2}
              onChange={(e) =>
                handleRestrictedSelect(e.target.value, setSelectedL2, ["Vše"])
              }
              options={level2SelectOptions}
              disabled={selectedL1 === "Vše" && !isRestrictedView}
              onClick={isRestrictedView ? () => openAuthPrompt("register") : undefined}
              helperText={
                isRestrictedView
                  ? "Dílčí obory jsou dostupné po registraci."
                  : undefined
              }
              selectClassName={`big-select${isRestrictedView ? " is-locked" : ""}`}
            />

            <div style={{ gridColumn: "1 / -1" }}>
              <SelectField
                className="control-group"
                label="Vyber ukazatel"
                value={selectedIndicator}
                onChange={(e) => setSelectedIndicator(e.target.value)}
                options={indicatorOptions}
                selectClassName="big-select"
              />
            </div>
        </div>
      </section>
      
      {/* --- QUICK OVERVIEW (Split Layout) --- */}
      <section className="card">
        <div className="card__header">
            <h3>{getContextTitle()}</h3>
        </div>
        <ChartHeader
          title={`Medián ${currentIndicatorMeta.label}`}
          description={medianDescription}
          tooltip={indicatorTooltipText}
        />

        <div className="chart-layout">

            {/* CHART */}
            <div className="chart-slot" style={{ height: "550px", width: "100%", position: "relative" }}>
                {/* Скелетон теперь накладывается ПОВЕРХ или заменяет видимость, но не удаляет узел графиков */}
                {loading && <div className="skeleton-block" style={{ position: "absolute", zIndex: 10, width: "100%", height: "100%" }} />}
                
                {/* Контейнер для графика ВСЕГДА в DOM. Мы управляем только его прозрачностью или данными */}
                <div 
                    className="chart" 
                    ref={barChartRef} 
                    style={{ 
                        height: "550px", 
                        width: "100%", 

                    }} 
                />

                {!loading && barData.length === 0 && (
                    <div className="data-hint">Žádná data pro vybraný filtr.</div>
                )}
            </div>

            {/* LEGEND / LIST */}
            <div className="chart-layout__sidebar">
                <h4>🗂️ Přehled kategorií</h4>
                {loading ? (
                  <ul className="chart-layout__list">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <li key={`skeleton-${index}`}>
                        <div className="skeleton-line" style={{ width: "65%", marginBottom: "6px" }} />
                        <div className="skeleton-line" style={{ width: "90%" }} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className="chart-layout__list">
                      {/* ДОБАВЛЯЕМ FILTER ПЕРЕД MAP */}
                      {barData
                        .filter(item => item.code && item.name && item.code !== "undefined") 
                        .map((item) => (
                          <li key={item.code}>
                              <div style={{ fontWeight: 'bold', color: '#027360' }}>
                                {item.code} - {formatValue(item.value, selectedIndicator)} 
                              </div>
                              <div style={{ color: '#666' }}>{item.name}</div>
                          </li>
                      ))}
                  </ul>
                )}
            </div>
        </div>
        {loading && <div className="data-hint">Načítám data...</div>}
      </section>

      {/* --- METHODOLOGY INFO --- */}
      <div style={{ marginBottom: '1.5rem' }}>
        <details style={{ background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #eee' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#027360' }}>ℹ️ Poznámky k metodice výpočtu ukazatelů</summary>
            
            <div style={{ marginTop: '1rem', fontSize: '0.9rem', lineHeight: '1.6', color: '#444' }}>
                <h4 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#0b1f2e' }}>📘 Transkripce výpočtu ukazatelů</h4>
                <p style={{ marginBottom: '1rem' }}>
                    Následující přehled shrnuje, jak byly jednotlivé ukazatele a pomocné položky dopočítány ze základních účetních dat.
                    Výpočty vycházejí z dat firem s ročním obratem nad 30 mil. Kč za období 2016–2025.
                </p>

                <h5 style={{ fontSize: '1rem', marginTop: '1rem', marginBottom: '0.5rem', borderBottom: '2px solid #eee', paddingBottom: '0.25rem' }}>
                    Základní odvozené veličiny
                </h5>
                <ul style={{ paddingLeft: '1.2rem', marginBottom: '1rem' }}>
                    <li>
                        <strong>Tržby celkem</strong> = Tržby za prodej zboží + Tržby za prodej vlastních výrobků a služeb
                    </li>
                    <li>
                        <strong>EBITDA</strong> = EBIT + Odpisy (pokud chybí, dopočteno jako součet EBIT + Odpisy DHM/DNM)
                    </li>
                </ul>

                <h5 style={{ fontSize: '1rem', marginTop: '1rem', marginBottom: '0.5rem', borderBottom: '2px solid #eee', paddingBottom: '0.25rem' }}>
                    Finanční ukazatele
                </h5>
                <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>ROA (Rentabilita aktiv)</strong> = EBIT / Aktiva celkem<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Efektivita využití aktiv pro tvorbu provozního zisku.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>ROE (Rentabilita vlastního kapitálu)</strong> = Výsledek hospodaření <u>před zdaněním</u> / Vlastní kapitál<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Výnosnost pro vlastníky (počítáno ze zisku před zdaněním).</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>ROS (Rentabilita tržeb)</strong> = Výsledek hospodaření <u>před zdaněním</u> / Tržby celkem<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Ziskovost tržeb před zdaněním.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>EBITDA marže</strong> = EBITDA / Tržby celkem<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Provozní výkonnost očištěná o odpisy.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>Provozní marže</strong> = EBIT / Tržby celkem<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Efektivita hlavní činnosti podniku.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>Zadluženost dlouhodobá</strong> = Dlouhodobé závazky / Aktiva celkem<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Podíl dlouhodobého cizího kapitálu na majetku.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>Zadluženost celková</strong> = (Aktiva celkem − Vlastní kapitál) / Aktiva celkem<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Celkový podíl cizích zdrojů financování.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>Likvidita běžná</strong> = Oběžná aktiva / Krátkodobé závazky<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Schopnost krýt krátkodobé závazky oběžnými aktivy.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>Likvidita pohotová</strong> = (Peněžní prostředky + Krátkodobé pohledávky + Dlouhodobé pohledávky) / Krátkodobé závazky<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Schopnost platit bez nutnosti prodeje zásob (konzervativní výpočet).</span>
                    </li>
                    <li>
                        <strong>Likvidita okamžitá</strong> = Peněžní prostředky na účtech / Krátkodobé závazky<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Okamžitá schopnost splácet dluhy hotovostí.</span>
                    </li>
                </ul>
            </div>
        </details>
      </div>

      {/* --- DETAILED ANALYSIS (BOXPLOTS) --- */}
      <section className="card">
        <div className="card__header">
            <h3> Podrobnější analýza (Rozptyl hodnot)</h3>
        </div>
        <ChartHeader
          title={`Rozptyl ${currentIndicatorMeta.label}`}
          description={`Boxplot ukazuje rozložení hodnot napříč ${contextLabel} za ${selectedPeriodLabel}. Box = 50 % firem (Q1–Q3), vousy = 80 % firem (p10–p90).`}
          tooltip={indicatorTooltipText}
        />
        <div className="chart-slot" style={{ height: "600px" }}>
          {loading && <div className="skeleton-block" />}
          {!loading && boxData.length > 0 && (
            <div className="chart" ref={boxChartRef} style={{ height: "600px" }} />
          )}
          {!loading && boxData.length === 0 && (
            <div className="data-hint">Žádná data pro vybraný filtr.</div>
          )}
        </div>
      </section>

      {/* --- TABLE --- */}
      <section className="card">
  <div
    className="card__header"
    style={{ 
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center",
      cursor: "pointer"
    }}
    onClick={() => setIsTableOpen(v => !v)}
  >
    <h3 style={{ margin: 0 }}>
      📋 Detailní tabulka statistik
    </h3>

    <span style={{ fontSize: "0.9rem", color: "#027360" }}>
      {isTableOpen ? "▲ Skrýt" : "▼ Zobrazit"}
    </span>
  </div>

  {isTableOpen && (
    <div className="table-wrapper" style={{ marginTop: "1rem" }}>
      <table className="data-table">
                <thead>
                    <tr>
                        <th>Kód</th>
                        <th>Název</th>
                        <th>N (Firmy)</th>
                        <th>Medián</th>
                        <th>Q1</th>
                        <th>Q3</th>
                        <th>p10</th>
                        <th>p90</th>
                    </tr>
                </thead>
                <tbody>
                    {benchmarkData.map((row, i) => {
                        const codeKey = currentContext === "sections" ? "Sekce" : currentContext === "lvl1" ? "1 úroveň" : "2 úroveň ID";
                        const nameKey = currentContext === "sections" ? "Sekce název" : currentContext === "lvl1" ? "1 úroveň název" : "2 úroveň název";
                        const k = selectedIndicator;
                        return (
                            <tr key={i}>
                                <td><b>{row[codeKey]}</b></td>
                                <td>{row[nameKey]}</td>
                                <td>{row.n}</td>
                                <td>{formatValue(row[`${k}__median`], selectedIndicator)}</td>
                                <td>{formatValue(row[`${k}__q1`], selectedIndicator)}</td>
                                <td>{formatValue(row[`${k}__q3`], selectedIndicator)}</td>
                                <td style={{color:'#888'}}>{formatValue(row[`${k}__p10`], selectedIndicator)}</td>
                                <td style={{color:'#888'}}>{formatValue(row[`${k}__p90`], selectedIndicator)}</td>
                            </tr>
                        );
                    })}
                </tbody>
                  </table>
    </div>
  )}
</section>


      {/* --- CAGR CHART SECTION --- */}
      <section className="card">
        <ChartHeader
          title={`${cagrMeta.label}`}
          description={`Průměrné roční tempo růstu (${cagrWindowLabel}) pro ${cagrTitle.toLowerCase()}. Výběr lze filtrovat podle sekce a skupiny NACE.`}
          tooltip={cagrMeta.description}
        />
        <div className="card__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>

                

            
            {/* CAGR Filter */}

  
<div className="filterscagr">
            
            <div className="control-group">

  <select
    className="big-select"
    value={cagrSekce}
    onChange={e => setCagrSekce(e.target.value)}
  >
    <option value="Vše">Všechny sekce</option>
    {allSections.map(s => (
      <option key={s.Sekce} value={s.Sekce}>
        {s.Sekce} – {s["Sekce název"]}
      </option>
    ))}
  </select></div>
<div className="control-group">
  <select
    className="big-select"
    value={cagrL1}
    onChange={e => setCagrL1(e.target.value)}
  >
    <option value="Vše">Všechny skupiny (L1)</option>

    {allL1
      .filter(l => l.Sekce === cagrSekce)
      .sort((a, b) => a["1 úroveň"] - b["1 úroveň"])
      .map(l => (
        <option key={l["1 úroveň"]} value={String(l["1 úroveň"])}>
          {l["1 úroveň"]} – {l["1 úroveň název"]}
        </option>
      ))}
  </select>
</div>
<div className="control-group">
  <select
    className="big-select"
    value={cagrWindowYears}
    onChange={e => setCagrWindowYears(Number(e.target.value))}
  >
    {CAGR_WINDOWS.map(w => (
      <option key={w.value} value={w.value}>{w.label}</option>
    ))}
  </select>
</div>
<div className="control-group" style={{ gridColumn: '1 / -1' }}>
  <select
    className="big-select"
    value={selectedCagrMetric}
    onChange={e => setSelectedCagrMetric(e.target.value)}
  >
    {CAGR_ORDER.map(m => (
      <option key={m} value={m}>{CAGR_LABELS[m]}</option>
    ))}
  </select>
</div>
</div>

        </div>

        <div className="chart-layout" style={{ marginTop: '20px' }}>

            {/* CHART */}
            <div className="chart-slot" style={{ height: "500px", width: "100%" }}>
              {cagrLoading && <div className="skeleton-block" />}
              {!cagrLoading && cagrBarData.length > 0 && (
                <div className="chart" ref={cagrChartRef} style={{ height: "500px", width: "100%" }} />
              )}
              {!cagrLoading && cagrBarData.length === 0 && (
                <div className="data-hint" style={{ textAlign: 'center', color: '#999' }}>
                  Žádná data CAGR pro tento výběr.
                </div>
              )}
            </div>

            {/* LEGEND / LIST (Reused style from first chart) */}
            <div className="chart-layout__sidebar">
                <h4>🏆 Top Růst</h4>
                {cagrLoading ? (
                  <ul className="chart-layout__list">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <li key={`cagr-skeleton-${index}`}>
                        <div className="skeleton-line" style={{ width: "65%", marginBottom: "6px" }} />
                        <div className="skeleton-line" style={{ width: "90%" }} />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className="chart-layout__list">
                    {cagrBarData.map((item) => (
                        <li key={item.code}>
                            <div style={{ fontWeight: 'bold', color: '#027360' }}>
                                {item.code} <span style={{color:'#333', marginLeft:'5px'}}>
                                    {/* Если значение в долях (0.05), умножаем на 100. Если уже в % (5.0), оставляем.
                                        Адаптируйте логику ниже под ваши данные. Обычно CAGR это 0.XX */}
                                    {(item.value * 100).toFixed(1)}%
                                </span>
                            </div>
                            <div style={{ color: '#666' }}>{item.name}</div>
                        </li>
                    ))}
                  </ul>
                )}
            </div>
        </div>
        {cagrLoading && (
          <div className="data-hint" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            Načítám data růstu...
          </div>
        )}
      </section>
      <section className="seo-info">
  <h2>O datech a metodice benchmarku</h2>

  <p>
    Tato stránka poskytuje statistický benchmark finančních ukazatelů
    českých podniků podle odvětví za období <strong>2016–2025</strong>.
    Datový soubor vychází z účetních výkazů více než 
    <strong> 25&nbsp;000 středních a velkých firem</strong>.
  </p>

  <p>
    Analýza umožňuje porovnávat výkonnost firem napříč sektory české ekonomiky
    z hlediska <strong>ziskovosti, likvidity, zadluženosti a provozní efektivity</strong>.
    Výsledky jsou prezentovány formou mediánů, kvartilů, rozptylu hodnot
    a dlouhodobé analýzy růstu (CAGR).
  </p>

  <ul>
    <li>- členění podle sekcí, oborů a klasifikace NACE</li>
    <li>- statistické rozdělení hodnot (p10–p90)</li>
    <li>- porovnání oborů v čase i mezi sebou</li>
  </ul>

  <p>
    Autorem analýzy je <strong>Ing. Ivan Bázin</strong>, investiční analytik (Genesis Capital), dříve M&amp;A analytik
    a advisor ve společnosti <a href="https://galikin.cz" target="_blank" rel="noopener noreferrer">Galik&nbsp;In</a>,
    specializující se na oceňování firem, oborový benchmarking a transakční poradenství.
    V případě dotazů k metodice nebo interpretaci dat jej můžete kontaktovat na
    <a href="mailto:info@finpilot.cz"> info@finpilot.cz</a>.
  </p>

  <p>
    Pokud vás zajímá <strong> detailní analýza konkrétního odvětví</strong>,
    můžete pokračovat na stránku
    <Link to="/oborove-statistiky"> oborových finančních statistik</Link>,
    kde najdete podrobný rozbor ukazatelů pro jednotlivé obory a NACE kategorie.
  </p>

  <p>
    Pro individuální posouzení vaší společnosti lze zároveň využít
    <strong> AI analytický nástroj FinPilot</strong>,
    který dokáže na základě účetních výkazů nebo IČO
    <strong> odhadnout hodnotu firmy a vyhodnotit její finanční kondici</strong>.
    Začněte na <Link to="/">hlavní stránce FinPilotu</Link>.
  </p>

  <h2>FAQ k benchmarku oborů</h2>
  <ul>
    <li><strong>Jak číst medián v benchmarku?</strong> Medián ukazuje typickou hodnotu metriky v dané skupině a je odolný vůči extrémům.</li>
    <li><strong>Co znamená p10–p90?</strong> Interval popisuje rozptyl běžných hodnot a pomáhá odhalit stabilní vs. volatilní obory.</li>
    <li><strong>Kde najdu detail k jednomu oboru?</strong> Využijte stránku <Link to="/oborove-statistiky">oborové statistiky</Link> pro hlubší drilldown.</li>
  </ul>
</section>



    </div>
  );
};

export default BenchmarkPage;
